import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Grid,
    Avatar,
} from '@material-ui/core';
import {
    SwapHoriz,
    DragIndicator,
} from '@material-ui/icons';
import { Icon as MdiIcon } from '@mdi/react';
import { mdiLicense } from '@mdi/js';
import './ShiftLineSelectionTable.scss';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
    Table,
    Whisper,
    Popover,
} from 'rsuite';
import { BidirectionalArrows, ShiftToolTip } from '@commonClient2/components';
import {
    ScheduleSource,
    DaysOfWeekNames,
    PeriodType,
    ShiftlineSelectionTableType,
    AnchorDay,
} from '../../../Enums';
import timeService from '../../../services/TimeService';
import shiftTemplateSegmentService from '../../../services/ShiftTemplateSegmentService';
import { handleMoveRow, handleToggleSelected } from '../../../store/bidSelection';

const { HeaderCell, Cell, Column } = Table;

/**
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData
 */
const renderEmptyCells = (rowData) => {
    const { weeklyHeight, id } = rowData;
    return (
        <div>
            {
                weeklyHeight.map((weeklyMax, week) => {
                    const content = [];
                    const numShiftsToAdd = weeklyMax;
                    for (let i = 0; i < numShiftsToAdd; i++) {
                        content.push(
                            <div
                                key={`heightHolder_x_${id}_${i}`}
                                className="empty"
                            />,
                        );
                    }

                    return (
                        <div
                            key={`heightHolder_x_${id}_container_${week}`}
                            className="single-day-shift-container"
                        >
                            {content}
                        </div>
                    );
                })
            }
        </div>
    );
};

/**
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData
 * @param {boolean?} disabled
 */
export const getPatternRowNumber = (rowData, disabled = false) => {
    const { weeklyHeight, id } = rowData;
    return (
        <div className="multi-day-shift-container">
            {weeklyHeight.map((weeklyMax, week) => {
                const content = [];
                content.push(
                    <>
                        <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={`RowNumber_${id}_${week + 1}`}
                            className={`row-number ${disabled ? 'unapproved-shift' : ''}`}
                        >
                            <span>
                                {week + 1}
                            </span>
                        </div>
                    </>,
                );

                const numShiftsToAdd = weeklyMax - 1;
                for (let i = 0; i < numShiftsToAdd; i++) {
                    content.push(
                        <div
                            key={`RowNumber_${id}_${week + 1}_${i}_empty`}
                            className="empty"
                        />,
                    );
                }
                return (
                    <div
                        key={`RowNumber_${id}_${week + 1}_container`}
                        className="single-day-shift-container"
                    >
                        {content}
                    </div>
                );
            })}
        </div>
    );
};

/**
 * @param {number} day
 * @param {BidProfile.ISchedulePatternsShiftTemplateDictionary} shiftsDictionary
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData
 * @param {number} dayCount
 * @param {boolean?} disabled
 */
export const getShiftsForDay = (day, shiftsDictionary, rowData, dayCount, daysPerPeriod = 7, disabled = false) => {
    const { shifts, weeklyHeight, id } = rowData;
    /**
     * @param {string} shiftTemplateId
     * @returns {BidProfile.IShiftTemplate}
     */
    const getShiftTemplate = (shiftTemplateId) => shiftsDictionary[shiftTemplateId];
    return (
        <div className="multi-day-shift-container">
            {weeklyHeight.map((weeklyMax, week) => {
                const content = [];
                let numShiftsToAdd = weeklyMax;
                if (shifts[week] && shifts[week][day]) {
                    shifts[week][day].map((s) => {
                        const shiftTemplate = getShiftTemplate(s.shiftTemplate);
                        if (shiftTemplate !== undefined) {
                            const shiftInfo = {
                                id: shiftTemplate.id,
                                timeString: timeService.calculateShiftTimes(shiftTemplate.startTime, shiftTemplate.endTime),
                                segments: shiftTemplate.segments.map((x) => ({
                                    id: x.id,
                                    timeString: timeService.calculateShiftTimes(x.startTime, x.endTime),
                                    orgJobPath: rowData.orgJobPath,
                                    segmentType: x.segmentTypeId,
                                    transferStrings: shiftTemplateSegmentService.generateTransferStrings(
                                        x.transferString || '',
                                        rowData.orgJobPath,
                                    ),
                                    skillAndCertifProfileLabels: x.segmentSCProfiles?.map((x) => x.label),
                                })),
                            };

                            content.push(
                                <ShiftToolTip
                                    element={(
                                        <div
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`DayShift_${id}_${week}_${day}_${shiftTemplate.id}_${numShiftsToAdd}`}
                                            className={`day-shift ${disabled ? 'unapproved-shift' : ''}`}
                                        >
                                            <span>
                                                {timeService.calculateShiftTimes(shiftTemplate.startTime, shiftTemplate.endTime)}
                                            </span>
                                            {shiftTemplateSegmentService.hasTransfer(shiftTemplate.segments) && <SwapHoriz />}
                                        </div>
                                    )}
                                    shiftInfos={shiftInfo}
                                />,
                            );
                            numShiftsToAdd--;
                        }
                    });
                }

                for (let i = 0; i < numShiftsToAdd; i++) {
                    content.push(
                        <div
                            key={`DayShift_x_${day}${new Date().getTime()}_${i}`}
                            className="empty"
                        />,
                    );
                }

                return (
                    <div
                        key={`DayShift_x_${day}${new Date().getTime()}_container_${week}`}
                        className={`single-day-shift-container ${(week * daysPerPeriod) + (day + 1) > dayCount ? 'sst-lightgray' : ''}`}
                    >
                        {content}
                    </div>
                );
            })}
        </div>
    );
};

/**
 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} scheduleTemplate
 * @param {boolean} approvedRow
 */
const getOrgJobWhisper = (
    { fullOrgJobPath, orgJobPath },
    approvedRow = false,
) => {
    const tooltip = (
        <Popover visible>
            {fullOrgJobPath}
        </Popover>
    );

    return (
        <Whisper
            placement="top"
            speaker={tooltip}
            delay={500}
            delayOpen={500}
            delayClose={500}
        >
            <span className={`ss-cell-text-content-reverse ${approvedRow ? '' : 'unapproved-shift'}`}>{orgJobPath}</span>
        </Whisper>
    );
};

/**
 * @typedef {{
 *  filteredItems: EmployeeBidProfile.IGroupedScheduleTemplateItems[],
 *  shiftsDictionary: BidProfile.ISchedulePatternsShiftTemplateDictionary,
 *  scheduleSource: ScheduleSource,
 *  anchorDay: DayOfWeek,
 *  dayCount: number,
 *  periodType: PeriodType,
 * }} ShiftlineSelectionTableExternalStoreData
 * @param {{
 *  isMultipleSelection: boolean,
 *  daysPerPeriod: number,
 *  type: ShiftlineSelectionTableType,
 *  externalStoreData: ShiftlineSelectionTableExternalStoreData,
 * } & import('rsuite').TableProps } props
 */
function ShiftLineSelectionTable({
    isMultipleSelection = false,
    daysPerPeriod = 7,
    type = ShiftlineSelectionTableType.EditSelection,
    externalStoreData = { // Use externalStoreData when data does not come from employeeBidSelection store
        filteredItems: [],
        shiftsDictionary: {},
        anchorDay: AnchorDay.Sunday,
        scheduleSource: ScheduleSource.ExcelFile,
        dayCount: 0,
        periodType: PeriodType.Weeks,
    },
    ...rest
}) {
    const dispatch = useDispatch();
    const isEditSelectionType = type === ShiftlineSelectionTableType.EditSelection;
    const isSelectionSummaryType = type === ShiftlineSelectionTableType.SelectionSummary;
    const isApprovedBidSummaryType = type === ShiftlineSelectionTableType.ApprovedBidSummary;
    const isUnawardedShiftlineReportType = type === ShiftlineSelectionTableType.UnawardedShiftlineReport;

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeBidSelection, employeeBidProfile, pendingTasks } = useSelector((state) => state);
    
    const {
        shifts: shiftsDictionary,
        filteredItems,
        rowHeight,
        employeeSelections,
        selectionFilters,
    } = employeeBidSelection;

    const { isGroupShiftlinesWithSameSchedules } = employeeBidProfile;

    let isPeriodTypeDay = false;
    let isWorkplanScheduleSource = false;
    let anchorDay = AnchorDay.Sunday;
    let dayCount = 0;
    if (isUnawardedShiftlineReportType) {
        const {
            scheduleSource,
            anchorDay: externalDataAnchorDay,
            dayCount: externalDataDayCount,
            periodType: externalDataPeriodType,
        } = externalStoreData;

        isWorkplanScheduleSource = scheduleSource === ScheduleSource.WorkplanManager;
        anchorDay = externalDataAnchorDay;
        dayCount = externalDataDayCount;
        isPeriodTypeDay = externalDataPeriodType === PeriodType.Days;
    } else {
        const {
            scheduleSource,
            anchorDay: storeDataAnchorDay,
            dayCount: storeDataDayCount,
            periodType,
        } = employeeBidSelection;

        isWorkplanScheduleSource = scheduleSource === ScheduleSource.WorkplanManager;
        anchorDay = storeDataAnchorDay;
        dayCount = storeDataDayCount;
        isPeriodTypeDay = periodType === PeriodType.Days;
    }

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} items
     * @param {boolean} isMoveUp
     */
    const handleMoveWithArrow = (rowData, items, isMoveUp) => {
        const findCondition = isGroupShiftlinesWithSameSchedules
            ? (x) => x.groupId === rowData.groupId
            : (x) => x.id === rowData.id;
        const elementIndex = items.findIndex(findCondition);
        const upDown = isMoveUp ? -1 : 1;
        const target = items[elementIndex + upDown];
        if (!target) {
            return;
        }

        dispatch(handleMoveRow(employeeBidSelection, rowData, target, items, isGroupShiftlinesWithSameSchedules));
    };

    /**
     * @param {{
     *  id: string,
     *  rowData: EmployeeBidProfile.IGroupedScheduleTemplateItems,
     *  renderCell: Function,
     *  canDrag: boolean,
     * }} param0
     */
    function DraggableCell({
        id, rowData, renderCell, canDrag = true, ...rest
    }) {
        if (canDrag) {
            const ref = useRef(null);
            const [{ canDrop, isOver }, drop] = useDrop({
                accept: 'ROW',
                collect: (/** @type {{ isOver: () => any; canDrop: () => any; }} */ monitor) => ({
                    isOver: monitor.isOver(),
                    canDrop: monitor.canDrop(),
                }),
                /**
                 * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} item
                 * @param {any} monitor
                 */
                drop(item, monitor) {
                    dispatch(handleMoveRow(employeeBidSelection, item, rowData, filteredItems, isGroupShiftlinesWithSameSchedules));
                },
            });

            const [{ isDragging }, drag] = useDrag({
                type: 'ROW',
                item: rowData,
                collect: (/** @type {{ isDragging: () => any; }} */ monitor) => ({
                    isDragging: monitor.isDragging(),
                }),
            });
            const isActive = canDrop && isOver;

            drag(drop(ref));

            /** @type {import('@material-ui/styles').CSSProperties} */
            const dynamicStyles = {
                opacity: isDragging ? 0.5 : 1,
                background: isActive ? '#DDDDDD33' : undefined,
            };

            return (
                <Cell
                    {...rest}
                    style={{ padding: 0 }}
                >
                    <div>
                        {renderEmptyCells(rowData)}
                    </div>
                    <div
                        ref={ref}
                        className="draggable-cell-style"
                        style={dynamicStyles}
                    >
                        {renderCell(rowData)}
                    </div>
                </Cell>
            );
        }
        return (
            <Cell
                {...rest}
            >
                <div
                    className={`undraggable-cell-style ${id === 'Order' ? 'order' : ''}`}
                >
                    {renderCell(rowData)}
                </div>
            </Cell>
        );
    }

    /**
     * @returns {EmployeeBidProfile.IGroupedScheduleTemplateItems[]}
     */
    function getTableData() {
        switch (type) {
            case ShiftlineSelectionTableType.ApprovedBidSummary:
                return filteredItems.filter((x) => x.approvedRow);
            case ShiftlineSelectionTableType.UnawardedShiftlineReport:
                return externalStoreData?.filteredItems || [];
            case ShiftlineSelectionTableType.EditSelection:
            case ShiftlineSelectionTableType.SelectionSummary:
            default:
                return filteredItems;
        }
    }

    /**
     * @returns {BidProfile.ISchedulePatternsShiftTemplateDictionary}
     */
    function getShiftsDictionary() {
        switch (type) {
            case ShiftlineSelectionTableType.UnawardedShiftlineReport:
                return externalStoreData?.shiftsDictionary || {};
            case ShiftlineSelectionTableType.EditSelection:
            case ShiftlineSelectionTableType.SelectionSummary:
            case ShiftlineSelectionTableType.ApprovedBidSummary:
            default:
                return shiftsDictionary;
        }
    }

    return (
        <Grid
            container
            className="shiftline-selection-table"
        >
            <DndProvider backend={HTML5Backend}>
                <div className="shiftline-rtable-container">
                    <Table
                        virtualized
                        height={850}
                        shouldUpdateScroll={false}
                        data={getTableData()}
                        id="shiftline-rtable"
                        rowHeight={rowHeight}
                        loading={!!pendingTasks}
                        {...rest}
                    >
                        {isEditSelectionType && (
                            <Column
                                key="draggable-icon"
                                flexGrow={0.2}
                                verticalAlign="middle"
                            >
                                <HeaderCell />
                                {
                                    // @ts-ignore
                                    <DraggableCell
                                        id="table-draggable-icon-container"
                                        renderCell={() => <DragIndicator />}
                                        canDrag={isMultipleSelection && isEditSelectionType}
                                    />
                                }
                            </Column>
                        )}
                        {isApprovedBidSummaryType && (
                            <Column
                                key="Awarding"
                                flexGrow={0.2}
                            >
                                <HeaderCell />
                                <Cell
                                    dataKey="Awarding"
                                    style={{ padding: 0 }}
                                >
                                    {
                                        /**
                                         * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData
                                         */
                                        (rowData) => (
                                            <div
                                                className="sst-awarding-icon"
                                            >
                                                {
                                                    rowData.approvedRow
                                                        ? (
                                                            <MdiIcon
                                                                path={mdiLicense}
                                                                size={1.5}
                                                            />
                                                        )
                                                        : ''
                                                }
                                            </div>
                                        )
                                    }
                                </Cell>
                            </Column>
                        )}
                        {(isEditSelectionType || isApprovedBidSummaryType || isSelectionSummaryType) && (
                            <Column
                                key="Order"
                                flexGrow={0.2}
                                verticalAlign="middle"
                            >
                                <HeaderCell />
                                {// @ts-ignore
                                    <DraggableCell
                                        id="Order"
                                        renderCell={
                                            (/** @type {EmployeeBidProfile.IGroupedScheduleTemplateItems} */ rowData) => {
                                                /**
                                                 * @type {any}
                                                 */
                                                let avatarLabel = '';
                                                const selectedId = isGroupShiftlinesWithSameSchedules
                                                    ? rowData.groupId
                                                    : rowData.id.toString();
                                                const index = employeeSelections.findIndex((x) => x === selectedId);
                                                if (index !== -1) {
                                                    avatarLabel = isMultipleSelection ? index + 1 : '✓';
                                                }
                                                let avatarStyle = avatarLabel ? 'selected-order ' : '';
                                                avatarStyle += (!isEditSelectionType && !isApprovedBidSummaryType) ? 'gray-boxes' : '';
                                                return (
                                                    <Avatar
                                                        className={`table-order-avatar ${avatarStyle}`}
                                                        onClick={() =>
                                                            dispatch(
                                                                handleToggleSelected(
                                                                    employeeBidSelection,
                                                                    isMultipleSelection,
                                                                    isGroupShiftlinesWithSameSchedules,
                                                                    rowData,
                                                                ),
                                                            )}
                                                        component="button"
                                                        disabled={!isEditSelectionType}
                                                    >
                                                        {avatarLabel}
                                                    </Avatar>
                                                );
                                            }
                                        }
                                        canDrag={isMultipleSelection && isEditSelectionType}
                                    />
                                }
                            </Column>
                        )}
                        {(isMultipleSelection && isEditSelectionType) && (
                            <Column
                                key="ActionArrows"
                                flexGrow={0.65}
                            >
                                <HeaderCell />
                                <Cell>
                                    {(/** @type {EmployeeBidProfile.IGroupedScheduleTemplateItems} */ rowData) => (
                                        <div className="table-arrows-avatar-container">
                                            <BidirectionalArrows
                                                direction="upDown"
                                                buttonGroupProps={{ orientation: 'vertical' }}
                                                firstButtonProps={{
                                                    onClick: () => handleMoveWithArrow(rowData, filteredItems, true),
                                                }}
                                                secondButtonProps={{
                                                    onClick: () => handleMoveWithArrow(rowData, filteredItems, false),
                                                }}
                                            />
                                        </div>
                                    )}
                                </Cell>
                            </Column>
                        )}
                        <Column
                            key="Schedule"
                            flexGrow={1.1}
                        >
                            <HeaderCell>
                                Schedule
                            </HeaderCell>
                            {// @ts-ignore
                                <DraggableCell
                                    id="Schedule"
                                    renderCell={(/** @type {EmployeeBidProfile.IGroupedScheduleTemplateItems} */ rowData) => {
                                        const tooltip = (
                                            <Popover visible>
                                                {rowData.scheduleTemplateNameList.map((x) => <div key={x}>{x}</div>)}
                                                {isWorkplanScheduleSource && (
                                                    <div>{rowData.workplanName}</div>)}
                                            </Popover>
                                        );

                                        return (
                                            <Whisper
                                                preventOverflow
                                                delay={500}
                                                delayOpen={500}
                                                delayClose={500}
                                                placement="top"
                                                speaker={tooltip}
                                            >
                                                <div className="schedule-container">
                                                    <div className="schedule-pattern-name-container">
                                                        <span
                                                            className={`schedule-pattern-name ${isWorkplanScheduleSource
                                                                ? 'text-md-regular' : 'ss-cell-text-content'}`}
                                                        >
                                                            {rowData.schedulePatternTemplate.name}
                                                        </span>
                                                        {rowData.quantity > 1 && (
                                                            <Avatar
                                                                className={`group-quantity-avatar ${isWorkplanScheduleSource
                                                                    ? 'smaller' : ''}`}
                                                            >
                                                                {rowData.quantity}
                                                            </Avatar>
                                                        )}
                                                    </div>
                                                    {
                                                        isWorkplanScheduleSource ? (
                                                            <span className="schedule-workplan-name text-xs-regular">
                                                                {rowData.workplanName}
                                                            </span>
                                                        ) : null
                                                    }
                                                </div>
                                            </Whisper>

                                        );
                                    }}
                                    canDrag={isMultipleSelection && isEditSelectionType}
                                />
                            }
                        </Column>
                        {(isEditSelectionType || isApprovedBidSummaryType || isSelectionSummaryType) && selectionFilters.showTotalHours && (
                            <Column
                                key="TotalScheduledTime"
                                flexGrow={1.35}
                            >
                                <HeaderCell>
                                    Total hours
                                </HeaderCell>
                                {// @ts-ignore
                                    <DraggableCell
                                        id="TotalScheduledTime"
                                        renderCell={
                                            /** @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData */
                                            (rowData) => {
                                                const tooltip = (
                                                    <Popover visible>
                                                        {rowData.totalScheduledTime}
                                                    </Popover>
                                                );

                                                return (
                                                    <Whisper
                                                        preventOverflow
                                                        delay={500}
                                                        delayOpen={500}
                                                        delayClose={500}
                                                        placement="top"
                                                        speaker={tooltip}
                                                    >
                                                        <span className="ss-cell-text-content">{rowData.totalScheduledTime}</span>
                                                    </Whisper>

                                                );
                                            }
                                        }
                                        canDrag={isMultipleSelection && isEditSelectionType}
                                    />
                                }
                            </Column>
                        )}
                        <Column
                            key="Location"
                            flexGrow={1.75}
                        >
                            <HeaderCell>
                                Location and job
                            </HeaderCell>
                            {// @ts-ignore
                                <DraggableCell
                                    id="Location"
                                    renderCell={
                                        /** @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData */
                                        (rowData) => getOrgJobWhisper(
                                            rowData,
                                            isEditSelectionType || isApprovedBidSummaryType || isUnawardedShiftlineReportType,
                                        )
                                    }
                                    canDrag={isMultipleSelection && isEditSelectionType}
                                />
                            }
                        </Column>
                        <Column
                            key="RowNumber"
                            flexGrow={1.25}
                        >
                            <HeaderCell>
                                Row number
                            </HeaderCell>
                            {// @ts-ignore
                                <DraggableCell
                                    id="RowNumber"
                                    renderCell={
                                        /** @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData */
                                        (rowData) => getPatternRowNumber(
                                            rowData,
                                        )
                                    }
                                    canDrag={isMultipleSelection && isEditSelectionType}
                                />
                            }
                        </Column>
                        {
                            Array.from(Array(7).keys()).map((x) => (
                                <Column
                                    key={`day_${x}`}
                                    flexGrow={1.25}
                                >
                                    <HeaderCell>
                                        {
                                            isPeriodTypeDay
                                                ? `${x + 1}`
                                                : DaysOfWeekNames[(x + anchorDay) % daysPerPeriod]
                                        }
                                    </HeaderCell>
                                    {// @ts-ignore
                                        <DraggableCell
                                            id={`day_${x}`}
                                            renderCell={
                                                /** @param {EmployeeBidProfile.IGroupedScheduleTemplateItems} rowData */
                                                (rowData) => getShiftsForDay(x, getShiftsDictionary(), rowData, dayCount, daysPerPeriod, isSelectionSummaryType)
                                            }
                                            canDrag={isMultipleSelection && isEditSelectionType}
                                        />
                                    }
                                </Column>
                            ))
                        }
                    </Table>
                </div>
            </DndProvider>
        </Grid>
    );
}

export default ShiftLineSelectionTable;
